import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Table, Tag, Button, Popconfirm, Modal, Select, Avatar, Tooltip, Badge, Empty, message as antMessage } from "antd";
import api from "../../../core/axios/axiosInstance";
import {
  fetchMyNumbers,
  releaseNumber,
  fetchMyAgents,
  assignNumberToAgent,
} from "../../../core/data/redux/slices/didlogicSlice";

const { Option } = Select;

const TYPE_COLOR = { local: "blue", mobile: "green", tollfree: "purple", voip: "cyan", cloud: "geekblue", onpremise: "orange" };
const TYPE_LABEL = { local: "Local", mobile: "Mobile", tollfree: "Toll-Free", voip: "VoIP", cloud: "Cloud", onpremise: "On-premise" };

function getInitials(agent) {
  if (!agent) return "?";
  return `${(agent.firstname || "")[0] || ""}${(agent.lastname || "")[0] || ""}`.toUpperCase() || "?";
}

const MyNumbers = () => {
  const dispatch = useDispatch();
  const { myNumbers, myNumbersTotal, myNumbersLoading, myAgents, agentsLoading, assigning } =
    useSelector((s) => s.didlogic);

  const [page, setPage] = useState(1);
  const [assignModal, setAssignModal] = useState(null); // { numberId, number, currentAgent }
  const [selectedAgentId, setSelectedAgentId] = useState(undefined);

  useEffect(() => {
    dispatch(fetchMyNumbers({ page, limit: 20 }));
    dispatch(fetchMyAgents());
  }, [dispatch, page]);

  const handleRelease = async (record) => {
    if (record.source === "pbx") {
      try {
        await api.delete(`/didlogic/numbers/pbx/${record.number}`);
        antMessage.success("Number released");
        dispatch(fetchMyNumbers({ page, limit: 20 }));
      } catch (err) {
        antMessage.error(err?.response?.data?.message || "Failed to release number");
      }
      return;
    }
    dispatch(releaseNumber(record._id));
  };

  const openAssignModal = (record) => {
    setAssignModal({
      record,
      numberId: record._id,
      number: record.number,
      currentAgent: record.assignedAgentId || null,
    });
    setSelectedAgentId(record.assignedAgentId?._id || record.assignedAgentId || undefined);
  };

  const handleAssignSave = async () => {
    if (!assignModal) return;
    const rec = assignModal.record;
    if (rec?.source === "pbx") {
      try {
        const fromId = rec.assignedAgentId?._id || null;
        await api.put("/admin/user/reassign-extension", {
          fromAgentId: fromId,
          toAgentId: selectedAgentId || null,
          extension: {
            extensionNumber: rec.extensionNumber,
            PBX_TELEPHONE: rec.number,
            assignedDeviceId: rec.assignedDeviceId || null,
            pbxType: rec.pbxType || "cloud",
            PBX_BASE_URL: rec.pbxBaseUrl || null,
            channels: rec.channels || 1,
            monthlyPrice: rec.monthlyPrice || 0,
            perMinuteRate: rec.perMinuteRate || 0,
          },
        });
        antMessage.success(selectedAgentId ? "Number assigned" : "Number returned to your pool");
      } catch (err) {
        antMessage.error(err?.response?.data?.message || "Failed to update assignment");
      }
      setAssignModal(null);
      setSelectedAgentId(undefined);
      dispatch(fetchMyNumbers({ page, limit: 20 }));
      return;
    }
    await dispatch(assignNumberToAgent({
      id: assignModal.numberId,
      agentId: selectedAgentId || null,
    }));
    setAssignModal(null);
    setSelectedAgentId(undefined);
    dispatch(fetchMyNumbers({ page, limit: 20 }));
  };

  const columns = [
    {
      title: "Phone Number",
      dataIndex: "number",
      key: "number",
      render: (v, r) => (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 20 }}>{r.flag || "🌐"}</span>
          <div>
            <div style={{ fontFamily: "monospace", fontWeight: 700, fontSize: 15, color: "#1a1a2e" }}>+{v}</div>
            <div style={{ fontSize: 11, color: "#8c8c8c" }}>{r.countryName || ""}</div>
          </div>
        </div>
      ),
    },
    {
      title: "Type",
      dataIndex: "numberType",
      key: "numberType",
      render: (v) => (
        <Tag color={TYPE_COLOR[v] || "default"} style={{ borderRadius: 20 }}>
          {TYPE_LABEL[v] || v || "—"}
        </Tag>
      ),
    },
    {
      title: "Area",
      dataIndex: "area",
      key: "area",
      render: (v) => v || <span style={{ color: "#bfbfbf" }}>—</span>,
    },
    {
      title: "Channels",
      dataIndex: "channels",
      key: "channels",
      align: "center",
      render: (v) => (
        <span style={{ fontWeight: 600, color: "#1a1a2e" }}>
          {v || 1}
          <span style={{ fontWeight: 400, color: "#8c8c8c", fontSize: 11 }}> ch</span>
        </span>
      ),
    },
    {
      title: "Activation",
      dataIndex: "ourActivationPrice",
      key: "ourActivationPrice",
      align: "right",
      render: (v, r) => r.source === "pbx" && !Number(v) ? <span style={{ color: "#bfbfbf" }}>—</span> : (
        <div style={{ fontWeight: 700, color: "#1a1a2e" }}>${Number(v || 0).toFixed(2)}</div>
      ),
    },
    {
      title: "Monthly Price",
      dataIndex: "ourMonthlyPrice",
      key: "ourMonthlyPrice",
      align: "right",
      render: (v, r) => r.source === "pbx" && !Number(v) ? <span style={{ color: "#bfbfbf" }}>—</span> : (
        <span style={{ fontWeight: 700, color: "#52c41a" }}>
          ${Number(v || 0).toFixed(2)}
          <span style={{ fontWeight: 400, color: "#8c8c8c", fontSize: 11 }}>/mo</span>
        </span>
      ),
    },
    {
      title: "Charge / Min",
      dataIndex: "perMinuteRate",
      key: "perMinuteRate",
      align: "right",
      render: (v) => Number(v) > 0 ? (
        <span style={{ fontWeight: 700, color: "#1a1a2e" }}>
          ${Number(v).toFixed(3)}
          <span style={{ fontWeight: 400, color: "#8c8c8c", fontSize: 11 }}>/min</span>
        </span>
      ) : (
        <Tooltip title="Charged by destination rate">
          <span style={{ color: "#8c8c8c", fontSize: 12 }}>By destination</span>
        </Tooltip>
      ),
    },
    {
      title: "Assigned Agent",
      dataIndex: "assignedAgentId",
      key: "assignedAgentId",
      render: (agent) => {
        if (!agent) return <span style={{ color: "#bfbfbf", fontSize: 13 }}>Not assigned</span>;
        const a = typeof agent === "object" ? agent : null;
        if (!a) return <Tag color="default">Assigned</Tag>;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Avatar size={28} style={{ background: "#667eea", fontSize: 11, fontWeight: 700 }}>
              {getInitials(a)}
            </Avatar>
            <div>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{a.firstname} {a.lastname}</div>
              <div style={{ fontSize: 11, color: "#8c8c8c" }}>{a.email}</div>
            </div>
          </div>
        );
      },
    },
    {
      title: "Purchased",
      dataIndex: "assignedAt",
      key: "assignedAt",
      render: (v) => v ? new Date(v).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—",
    },
    {
      title: "Actions",
      key: "action",
      render: (_, r) => r.source === "pbx" && r.isPrimary ? (
        <Tag style={{ borderRadius: 20 }}>Main number</Tag>
      ) : (
        <div style={{ display: "flex", gap: 8 }}>
          <Button
            size="small"
            style={{ borderRadius: 8, borderColor: "#667eea", color: "#667eea" }}
            onClick={() => openAssignModal(r)}
          >
            <i className="ti ti-user-check" style={{ marginRight: 4 }} />
            {r.assignedAgentId ? "Reassign" : "Assign Agent"}
          </Button>
          <Popconfirm
            title="Release this number?"
            description={r.source === "pbx" ? "The number will be removed from your account and your agents. Ask the admin to add it again to get it back." : "The number will go back to the available pool and your agent will lose access."}
            onConfirm={() => handleRelease(r)}
            okText="Release"
            okButtonProps={{ danger: true }}
            cancelText="Cancel"
          >
            <Button danger size="small" style={{ borderRadius: 8 }}>
              <i className="ti ti-trash" style={{ marginRight: 4 }} />Release
            </Button>
          </Popconfirm>
        </div>
      ),
      width: 220,
    },
  ];

  // Summary stats
  const assigned = myNumbers.filter((n) => n.assignedAgentId).length;
  const unassigned = myNumbers.length - assigned;

  return (
    <div className="page-wrapper">
      <div className="content">

        {/* Page header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
          <div>
            <h4 style={{ fontWeight: 700, marginBottom: 4 }}>
              <i className="ti ti-phone-check" style={{ marginRight: 8, color: "#667eea" }} />
              My Numbers
            </h4>
            <p style={{ color: "#8c8c8c", marginBottom: 0, fontSize: 13 }}>
              Phone numbers purchased for your account
            </p>
          </div>
          <a href="/numbers/browse-numbers" className="btn btn-primary btn-sm" style={{ borderRadius: 8 }}>
            <i className="ti ti-plus" style={{ marginRight: 6 }} />
            Get More Numbers
          </a>
        </div>

        {/* Stats bar */}
        {myNumbers.length > 0 && (
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
            gap: 14,
            marginBottom: 24,
          }}>
            {[
              { label: "Total Numbers", value: myNumbersTotal, icon: "ti-phone", color: "#667eea", bg: "#f0f0ff" },
              { label: "Assigned to Agent", value: assigned, icon: "ti-user-check", color: "#52c41a", bg: "#f6ffed" },
              { label: "Unassigned", value: unassigned, icon: "ti-user-x", color: "#fa8c16", bg: "#fff7e6" },
            ].map((s) => (
              <div key={s.label} style={{
                background: "#fff",
                border: "1px solid #f0f0f0",
                borderRadius: 12,
                padding: "16px 20px",
                display: "flex",
                alignItems: "center",
                gap: 14,
              }}>
                <div style={{
                  width: 44, height: 44, borderRadius: 12,
                  background: s.bg, display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <i className={`ti ${s.icon}`} style={{ fontSize: 22, color: s.color }} />
                </div>
                <div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: "#1a1a2e" }}>{s.value}</div>
                  <div style={{ fontSize: 12, color: "#8c8c8c" }}>{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Table */}
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #f0f0f0", overflow: "hidden" }}>
          <Table
            dataSource={myNumbers}
            columns={columns}
            rowKey="_id"
            loading={myNumbersLoading}
            pagination={{
              total: myNumbersTotal,
              pageSize: 20,
              current: page,
              onChange: setPage,
              showTotal: (t) => `${t} numbers`,
              style: { padding: "12px 16px" },
            }}
            locale={{
              emptyText: (
                <div style={{ padding: "60px 0", textAlign: "center" }}>
                  <i className="ti ti-phone-off" style={{ fontSize: 48, color: "#bfbfbf", display: "block", marginBottom: 16 }} />
                  <p style={{ color: "#8c8c8c", marginBottom: 12 }}>No numbers purchased yet.</p>
                  <a href="/numbers/browse-numbers" className="btn btn-primary btn-sm" style={{ borderRadius: 8 }}>
                    Browse Available Numbers
                  </a>
                </div>
              ),
            }}
          />
        </div>

        {/* Assign Agent Modal */}
        <Modal
          title={null}
          open={!!assignModal}
          onOk={handleAssignSave}
          onCancel={() => { setAssignModal(null); setSelectedAgentId(undefined); }}
          confirmLoading={assigning}
          okText="Save Assignment"
          cancelText="Cancel"
          okButtonProps={{ style: { background: "linear-gradient(135deg,#667eea,#764ba2)", border: "none", borderRadius: 8 } }}
          cancelButtonProps={{ style: { borderRadius: 8 } }}
          width={480}
        >
          {assignModal && (
            <div>
              {/* Modal header */}
              <div style={{
                background: "linear-gradient(135deg,#667eea 0%,#764ba2 100%)",
                borderRadius: "8px 8px 0 0",
                padding: "20px 24px",
                margin: "-20px -24px 24px",
                color: "#fff",
              }}>
                <h5 style={{ color: "#fff", margin: 0, fontWeight: 700 }}>
                  <i className="ti ti-user-check" style={{ marginRight: 8 }} />
                  Assign Number to Agent
                </h5>
                <p style={{ color: "rgba(255,255,255,0.75)", margin: "4px 0 0", fontSize: 13 }}>
                  +{assignModal.number}
                </p>
              </div>

              <label style={{ display: "block", fontWeight: 600, marginBottom: 8, color: "#1a1a2e" }}>
                Select Agent
              </label>
              <Select
                showSearch
                placeholder="Search and select an agent..."
                style={{ width: "100%" }}
                size="large"
                value={selectedAgentId || undefined}
                onChange={(v) => setSelectedAgentId(v)}
                loading={agentsLoading}
                allowClear
                filterOption={(input, option) =>
                  option?.searchlabel?.toLowerCase().includes(input.toLowerCase())
                }
                optionLabelProp="label"
              >
                {myAgents
                  .filter((a) => a.isVerified === true)   // exclude Pending Verification agents
                  .map((a) => (
                    <Option
                      key={a._id}
                      value={a._id}
                      label={`${a.firstname} ${a.lastname}`}
                      searchlabel={`${a.firstname} ${a.lastname} ${a.email}`}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "4px 0" }}>
                        <Avatar size={28} style={{ background: "#667eea", fontSize: 11, fontWeight: 700 }}>
                          {getInitials(a)}
                        </Avatar>
                        <div>
                          <div style={{ fontWeight: 500 }}>{a.firstname} {a.lastname}</div>
                          <div style={{ fontSize: 11, color: "#8c8c8c" }}>{a.email}</div>
                        </div>
                      </div>
                    </Option>
                  ))}
              </Select>

              {myAgents.filter((a) => a.isVerified).length === 0 && !agentsLoading && (
                <p style={{ color: "#fa8c16", fontSize: 12, marginTop: 10 }}>
                  <i className="ti ti-alert-triangle" style={{ marginRight: 4 }} />
                  {myAgents.length > 0
                    ? "No verified agents yet — agents must complete their account setup first."
                    : "No agents found. Add agents from the Agents page first."}
                </p>
              )}

              <div style={{
                marginTop: 16,
                padding: "12px 16px",
                background: "#f8f9ff",
                borderRadius: 10,
                fontSize: 12,
                color: "#595959",
              }}>
                <i className="ti ti-info-circle" style={{ marginRight: 6, color: "#667eea" }} />
                The assigned agent will be able to use this number for outbound calls. Clear the selection to unassign.
              </div>
            </div>
          )}
        </Modal>
      </div>
    </div>
  );
};

export default MyNumbers;
